// Production master data — data access (docs/design/production.md, ADR-029).
//
//   work centers  table work_centers (RLS writes with production.master.manage),
//                 one page per company in code order (work_centers_code_key), ≤ 200 rows.
//   BOMs          rpc bom_page(status, term, after_code, after_version, limit 51) —
//                 keyset on boms_list_idx; bom_document(id) — header, lines and the
//                 item's versions in one round-trip; bom_where_used(item).
//   routings      rpc routing_page(company, …) on routings_list_idx; routing_document(id).
//   writes        save_ / copy_ / approve_ / obsolete_ / delete_*_draft RPCs; saves send
//                 expected_revision so a stale editor cannot overwrite a colleague.
// Quantities travel as decimal strings (Postgres numeric), never floats.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export const versionStatuses = ["draft", "approved", "obsolete"] as const;
export type VersionStatus = (typeof versionStatuses)[number];
export type VersionFilter = "current" | "all" | VersionStatus;
export const isVersionFilter = (v: unknown): v is VersionFilter =>
  v === "current" || v === "all" || (versionStatuses as readonly unknown[]).includes(v);

export const versionTone = { draft: "neutral", approved: "success", obsolete: "neutral" } as const satisfies Record<VersionStatus, string>;

export const workCenterKinds = ["machine", "line", "team", "subcontractor"] as const;
export type WorkCenterKind = (typeof workCenterKinds)[number];

export interface WorkCenterRow {
  id: string;
  company_id: string;
  code: string;
  name: string;
  kind: WorkCenterKind;
  supply_warehouse_id: string | null;
  supply_location_id: string | null;
  hours_per_day: string | number;
  is_active: boolean;
  supply_warehouse: { code: string } | null;
  supply_location: { code: string } | null;
  /** Subcontractor work centers (docs/design/subcontracting.md). */
  supplier_id: string | null;
  service_item_id: string | null;
  subcontract_price: string | number | null;
  subcontract_lead_days: number | null;
  supplier: { code: string; name: string } | null;
  service_item: { code: string; description: string } | null;
}

export interface WorkCenterInput {
  name: string;
  kind: WorkCenterKind;
  supply_warehouse_id: string | null;
  supply_location_id: string | null;
  hours_per_day: string;
  is_active: boolean;
  supplier_id: string | null;
  service_item_id: string | null;
  subcontract_price: string | null;
  subcontract_lead_days: number | null;
}

export interface VersionRef {
  id: string;
  version: number;
  status: VersionStatus;
  valid_from: string;
  valid_to: string | null;
}

export interface BomRow {
  id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  version: number;
  status: VersionStatus;
  valid_from: string;
  valid_to: string | null;
  base_qty: string | number;
  base_uom_code: string;
  line_count: number;
  updated_at: string;
}

export interface BomLine {
  id: string;
  line_no: number;
  component_item_id: string;
  component_code: string;
  component_description: string;
  component_procurement_type: "purchase" | "manufacture";
  qty: string | number;
  uom_id: string;
  uom_code: string;
  uom_decimals: number;
  base_qty: string | number;
  base_uom_code: string;
  scrap_pct: string | number;
  issue_method: "manual" | "backflush";
  operation_seq: number | null;
  note: string | null;
}

export interface BomDocument {
  id: string;
  /** EBOM → MBOM (ADR-043): the engineering revision this version came from, and the item's released one. */
  engineering_bom_id?: string | null;
  engineering_rev?: string | null;
  released_ebom?: { id: string; rev: string } | null;
  item_id: string;
  item_code: string;
  item_description: string;
  base_uom_code: string;
  base_uom_decimals: number;
  version: number;
  status: VersionStatus;
  valid_from: string;
  valid_to: string | null;
  base_qty: string | number;
  note: string | null;
  line_count: number;
  revision: number;
  approved_at: string | null;
  approved_by_name: string | null;
  updated_at: string;
  lines: BomLine[];
  versions: VersionRef[];
}

export interface BomLineInput {
  component_item_id: string;
  qty: string;
  scrap_pct?: string;
  issue_method?: "manual" | "backflush";
  operation_seq?: number | null;
  note?: string | null;
}

export interface WhereUsed {
  has_more: boolean;
  uses: {
    id: string;
    item_code: string;
    description: string;
    version: number;
    status: VersionStatus;
    qty: string | number;
    uom_code: string;
  }[];
}

export interface RoutingRow {
  id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  version: number;
  status: VersionStatus;
  valid_from: string;
  valid_to: string | null;
  operation_count: number;
  updated_at: string;
}

export interface RoutingOperation {
  id: string;
  seq: number;
  work_center_id: string;
  work_center_code: string;
  work_center_name: string;
  description: string;
  setup_minutes: string | number;
  run_minutes_per_unit: string | number;
}

export interface RoutingDocument {
  id: string;
  company_id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  base_uom_code: string;
  version: number;
  status: VersionStatus;
  valid_from: string;
  valid_to: string | null;
  note: string | null;
  operation_count: number;
  revision: number;
  approved_at: string | null;
  approved_by_name: string | null;
  updated_at: string;
  operations: RoutingOperation[];
  versions: VersionRef[];
}

export interface OperationInput {
  seq: number;
  work_center_id: string;
  description: string;
  setup_minutes?: string;
  run_minutes_per_unit?: string;
}

export interface VersionHeaderInput {
  valid_from?: string;
  valid_to?: string | null;
  base_qty?: string;
  note?: string | null;
  expected_revision?: number;
}

export interface SaveResult {
  id: string;
  version: number;
  status: VersionStatus;
  revision: number;
}

export interface VersionListParams {
  status: VersionFilter;
  q: string;
  /** Column filters and sort (bom_page / routing_page spec, versionListColumns). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export const MASTER_PAGE = 50;
const WORK_CENTER_LIMIT = 200;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}
const demoOff = () => new Error("Production master data is not available in demo mode");

// ---------------------------------------------------------------------------
// Work centers
// ---------------------------------------------------------------------------
const workCenterColumns =
  "id,company_id,code,name,kind,supply_warehouse_id,supply_location_id,hours_per_day,is_active," +
  "supplier_id,service_item_id,subcontract_price,subcontract_lead_days," +
  "supply_warehouse:warehouses!work_centers_warehouse_fkey(code),supply_location:locations!work_centers_location_fkey(code)," +
  "supplier:business_partners!work_centers_supplier_fkey(code,name),service_item:items!work_centers_service_item_fkey(code,description)";

async function listWorkCenters(companyId: string, inactive: boolean, signal: AbortSignal): Promise<Page<WorkCenterRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  let query = db()
    .from("work_centers")
    .select(workCenterColumns)
    .eq("company_id", companyId)
    .order("code")
    .limit(WORK_CENTER_LIMIT)
    .abortSignal(signal);
  if (!inactive) query = query.eq("is_active", true);
  const { data, error } = await query;
  if (error) throw error;
  return { rows: data as unknown as WorkCenterRow[], nextCursor: null };
}

async function getWorkCenter(id: string, signal: AbortSignal): Promise<WorkCenterRow | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().from("work_centers").select(workCenterColumns).eq("id", id).abortSignal(signal).maybeSingle();
  if (error) throw error;
  return data as unknown as WorkCenterRow | null;
}

export async function createWorkCenter(tenantId: string, companyId: string, code: string, input: WorkCenterInput): Promise<string> {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db()
    .from("work_centers")
    .insert({ tenant_id: tenantId, company_id: companyId, code, ...input })
    .select("id")
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function updateWorkCenter(id: string, input: WorkCenterInput): Promise<void> {
  if (isDemoMode) throw demoOff();
  const { error } = await db().from("work_centers").update(input).eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// BOMs
// ---------------------------------------------------------------------------

export async function listBoms(
  p: VersionListParams,
  cursor: string | null,
  signal: AbortSignal,
  size = MASTER_PAGE,
): Promise<Page<BomRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("bom_page", {
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (BomRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getBom(id: string, signal: AbortSignal): Promise<BomDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("bom_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as BomDocument | null) ?? null;
}

async function getWhereUsed(itemId: string, signal: AbortSignal): Promise<WhereUsed> {
  if (isDemoMode) return { has_more: false, uses: [] };
  const { data, error } = await db().rpc("bom_where_used", { p_item_id: itemId }).abortSignal(signal);
  if (error) throw error;
  return data as WhereUsed;
}

export async function saveBom(id: string | null, itemId: string, header: VersionHeaderInput, lines: BomLineInput[]): Promise<SaveResult> {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db().rpc("save_bom", { p_id: id, p_item_id: itemId, p_header: header, p_lines: lines });
  if (error) throw error;
  return data as SaveResult;
}

export type VersionAction = "approve" | "copy" | "obsolete" | "delete";

export async function bomAction(id: string, action: VersionAction, revision: number): Promise<SaveResult | null> {
  if (isDemoMode) throw demoOff();
  const call =
    action === "approve"
      ? db().rpc("approve_bom", { p_id: id, p_expected_revision: revision })
      : db().rpc({ copy: "copy_bom", obsolete: "obsolete_bom", delete: "delete_bom_draft" }[action], { p_id: id });
  const { data, error } = await call;
  if (error) throw error;
  return (data as SaveResult | null) ?? null;
}

// ---------------------------------------------------------------------------
// Routings
// ---------------------------------------------------------------------------
export async function listRoutings(
  companyId: string,
  p: VersionListParams,
  cursor: string | null,
  signal: AbortSignal,
  size = MASTER_PAGE,
): Promise<Page<RoutingRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("routing_page", {
      p_company_id: companyId,
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (RoutingRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getRouting(id: string, signal: AbortSignal): Promise<RoutingDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("routing_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as RoutingDocument | null) ?? null;
}

export async function saveRouting(
  id: string | null,
  companyId: string,
  itemId: string,
  header: VersionHeaderInput,
  operations: OperationInput[],
): Promise<SaveResult> {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db().rpc("save_routing", {
    p_id: id,
    p_company_id: companyId,
    p_item_id: itemId,
    p_header: header,
    p_operations: operations,
  });
  if (error) throw error;
  return data as SaveResult;
}

export async function routingAction(id: string, action: VersionAction, revision: number): Promise<SaveResult | null> {
  if (isDemoMode) throw demoOff();
  const call =
    action === "approve"
      ? db().rpc("approve_routing", { p_id: id, p_expected_revision: revision })
      : db().rpc({ copy: "copy_routing", obsolete: "obsolete_routing", delete: "delete_routing_draft" }[action], { p_id: id });
  const { data, error } = await call;
  if (error) throw error;
  return (data as SaveResult | null) ?? null;
}

// ---------------------------------------------------------------------------
// Query options
// ---------------------------------------------------------------------------
export const productionKeys = {
  all: ["production"] as const,
  workCenters: (companyId: string, inactive: boolean) => [...productionKeys.all, "work-centers", companyId, inactive] as const,
  workCenter: (id: string) => [...productionKeys.all, "work-center", id] as const,
  boms: (p: VersionListParams) => [...productionKeys.all, "boms", p] as const,
  bom: (id: string) => [...productionKeys.all, "bom", id] as const,
  whereUsed: (itemId: string) => [...productionKeys.all, "where-used", itemId] as const,
  routings: (companyId: string, p: VersionListParams) => [...productionKeys.all, "routings", companyId, p] as const,
  routing: (id: string) => [...productionKeys.all, "routing", id] as const,
};

// Master data changes rarely: minutes of staleTime, background refetch.
const STALE = 120_000;

export function workCenterListQuery(companyId: string, inactive: boolean) {
  return infiniteQueryOptions({
    queryKey: productionKeys.workCenters(companyId, inactive),
    queryFn: ({ signal }) => listWorkCenters(companyId, inactive, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<WorkCenterRow>) => last.nextCursor,
    staleTime: STALE,
    enabled: !!companyId,
  });
}

export function workCenterDetailQuery(id: string) {
  return queryOptions({ queryKey: productionKeys.workCenter(id), queryFn: ({ signal }) => getWorkCenter(id, signal), staleTime: STALE });
}

export function bomListQuery(p: VersionListParams) {
  return infiniteQueryOptions({
    queryKey: productionKeys.boms(p),
    queryFn: ({ pageParam, signal }) => listBoms(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<BomRow>) => last.nextCursor,
    staleTime: STALE,
  });
}

export function bomDetailQuery(id: string) {
  return queryOptions({ queryKey: productionKeys.bom(id), queryFn: ({ signal }) => getBom(id, signal), staleTime: STALE });
}

export function whereUsedQuery(itemId: string) {
  return queryOptions({
    queryKey: productionKeys.whereUsed(itemId),
    queryFn: ({ signal }) => getWhereUsed(itemId, signal),
    staleTime: STALE,
  });
}

export function routingListQuery(companyId: string, p: VersionListParams) {
  return infiniteQueryOptions({
    queryKey: productionKeys.routings(companyId, p),
    queryFn: ({ pageParam, signal }) => listRoutings(companyId, p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<RoutingRow>) => last.nextCursor,
    staleTime: STALE,
    enabled: !!companyId,
  });
}

export function routingDetailQuery(id: string) {
  return queryOptions({ queryKey: productionKeys.routing(id), queryFn: ({ signal }) => getRouting(id, signal), staleTime: STALE });
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
const rules: [RegExp, string][] = [
  [/was changed by someone else/, "prod.err.stale"],
  [/is not a manufactured item/, "prod.err.notManufactured"],
  [/^Only draft .* can be changed/, "prod.err.notDraft"],
  [/^Only draft .* can be approved/, "prod.err.notDraft"],
  [/^Only approved .* can be made obsolete/, "prod.err.notApproved"],
  [/starts on or after/, "prod.err.laterVersion"],
  [/would contain itself/, "prod.err.cycle"],
  [/^A BOM needs at least one component/, "prod.err.noLines"],
  [/^A routing needs at least one operation/, "prod.err.noOperations"],
  [/^A component is inactive/, "prod.err.componentInactive"],
  [/^A work center is inactive/, "prod.err.workCenterInactive"],
  [/^Valid-to must be after valid-from/, "prod.err.validity"],
  [/^The base quantity must be above zero/, "prod.err.baseQty"],
  [/^The supply warehouse must belong/, "prod.err.supplyWarehouse"],
  [/work_centers_code_key|duplicate key/, "prod.err.duplicateCode"],
  [/^Missing permission|row-level security/, "prod.err.permission"],
];

const lineRules: [RegExp, string][] = [
  [/^component and quantity are required/, "prod.err.incompleteLine"],
  [/^quantity must be above zero/, "po.err.qtyPositive"],
  [/^an item cannot be its own component/, "prod.err.selfComponent"],
  [/^services cannot be components/, "prod.err.serviceComponent"],
  [/^component is inactive/, "prod.err.componentInactiveLine"],
  [/^scrap must be/, "prod.err.scrap"],
  [/^sequence, work center and description are required/, "prod.err.incompleteOperation"],
  [/^sequence appears twice/, "prod.err.duplicateSeq"],
  [/^work center not found/, "prod.err.workCenterNotFound"],
  [/^work center is inactive/, "prod.err.workCenterInactiveLine"],
];

export function prodErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const line = /^(Line|Operation) (\d+): (.+)$/.exec(message);
  if (line) {
    const rule = lineRules.find(([re]) => re.test(line[3] ?? ""));
    return tr("prod.err.line", { line: line[2] ?? "", message: rule ? tr(rule[1]) : (line[3] ?? "") });
  }
  if (/would contain itself/.test(message)) return tr("prod.err.cycle", { path: message.split(": ").slice(1).join(": ") });
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}

// ---------------------------------------------------------------------------
// Multi-level BOM (docs/design/production.md §10)
//   explosion   rpc bom_explosion(item, company, date, qty) — approved versions
//               valid on the date, all levels (≤ 20 levels / 2000 rows), one
//               round-trip; bom_lines PK + boms version walk per node.
//   where-used  rpc bom_where_used_tree(item, date) — up to the top items
//               (bom_lines_component_idx per level, ≤ 1000 rows).
// ---------------------------------------------------------------------------

export interface ExplosionRow {
  depth: number;
  item_id: string;
  parent_item_id: string | null;
  line_no: number | null;
  item_code: string;
  item_description: string;
  item_type: string;
  procurement_type: "purchase" | "manufacture";
  uom: string;
  uom_decimals: number;
  qty_per: number | string;
  qty: number | string;
  scrap_pct: number | string | null;
  issue_method: "manual" | "backflush" | null;
  bom_id: string | null;
  bom_version: number | null;
  missing_bom: boolean;
  cycle: boolean;
  lead_time: number;
  cum_lead_time: number;
}

export interface Explosion {
  date: string;
  qty: number | string;
  truncated: boolean;
  rows: ExplosionRow[];
}

export interface WhereUsedTreeRow {
  depth: number;
  item_id: string;
  child_id: string;
  item_code: string;
  item_description: string;
  item_type: string;
  bom_id: string;
  bom_version: number;
  qty_per: number | string;
  top: boolean;
}

export function bomExplosionQuery(itemId: string, date: string, qty: string, companyId: string) {
  return queryOptions({
    queryKey: [...productionKeys.all, "explosion", itemId, date, qty, companyId] as const,
    queryFn: async ({ signal }): Promise<Explosion> => {
      if (isDemoMode) return { date, qty, truncated: false, rows: [] };
      const { data, error } = await db()
        .rpc("bom_explosion", { p_item_id: itemId, p_date: date, p_qty: qty, p_company_id: companyId })
        .abortSignal(signal);
      if (error) throw error;
      return data as Explosion;
    },
    staleTime: STALE,
  });
}

export function whereUsedTreeQuery(itemId: string, date: string) {
  return queryOptions({
    queryKey: [...productionKeys.all, "where-used-tree", itemId, date] as const,
    queryFn: async ({ signal }): Promise<{ date: string; truncated: boolean; rows: WhereUsedTreeRow[] }> => {
      if (isDemoMode) return { date, truncated: false, rows: [] };
      const { data, error } = await db().rpc("bom_where_used_tree", { p_item_id: itemId, p_date: date }).abortSignal(signal);
      if (error) throw error;
      return data as { date: string; truncated: boolean; rows: WhereUsedTreeRow[] };
    },
    staleTime: STALE,
  });
}
